/**
 * Shipment statuses as the UI sees them.
 *
 * One status is never surfaced, and the label shown for a status can differ from
 * the backend's name, so every consumer needs the same mapping — the status
 * filter dropdown and the Shipment Overview widgets both read it here.
 */
import { useEffect, useState } from "react";
import httpClient from "../../../../../api/httpClient";

// API status → label shown to the user (null = hidden from the UI entirely).
export const STATUS_API_TO_VIEW = {
  // Completed is reserved for a future, distinct status and nothing sets it yet — hidden from
  // the filter for the same reason Rejected used to be (a status nothing currently persists).
  Completed: null,
};

const STATUS_VIEW_TO_API = Object.fromEntries(
  Object.entries(STATUS_API_TO_VIEW)
    .filter(([, view]) => view)
    .map(([api, view]) => [view, api])
);

export const FALLBACK_API_STATUSES = [
  "New", "Assigned", "Unassigned", "Tendered", "Accepted",
  "Rejected", "Recalled", "Delivered", "Completed", "In Transit", "Cancelled",
];

export const toViewStatus = (apiStatus) => STATUS_API_TO_VIEW[apiStatus] ?? apiStatus;
export const isHiddenStatus = (apiStatus) => STATUS_API_TO_VIEW[apiStatus] === null;
export const toApiStatus = (viewStatus) => STATUS_VIEW_TO_API[viewStatus] ?? viewStatus;

const visibleStatuses = (statuses) =>
  Array.from(
    new Set(
      (Array.isArray(statuses) ? statuses : [])
        .map((s) => String(s ?? "").trim())
        .filter(Boolean)
        .filter((s) => !isHiddenStatus(s))
    )
  );

// Cached so the widgets and the filter dropdown share a single request.
let statusesPromise = null;

const loadStatuses = () => {
  if (statusesPromise) return statusesPromise;

  statusesPromise = httpClient
    .get("/api/shipments/statuses")
    .then((res) => (Array.isArray(res.data) ? res.data : []))
    .catch((err) => {
      console.error("Failed to load shipment statuses:", err);
      statusesPromise = null; // let the next mount retry
      return FALLBACK_API_STATUSES;
    });

  return statusesPromise;
};

/**
 * Visible shipment statuses: `apiStatuses` for requests/state, `viewStatuses`
 * for anything shown to the user.
 */
export const useShipmentStatuses = () => {
  const [apiStatuses, setApiStatuses] = useState([]);

  useEffect(() => {
    let cancelled = false;

    loadStatuses().then((statuses) => {
      if (cancelled) return;
      const visible = visibleStatuses(statuses);
      setApiStatuses(visible.length ? visible : visibleStatuses(FALLBACK_API_STATUSES));
    });

    return () => { cancelled = true; };
  }, []);

  return { apiStatuses, viewStatuses: apiStatuses.map(toViewStatus) };
};
